import { Group, type Object3D } from 'three';

/**
 * Hinge model.
 *
 * A hinge is a *render-time* abstraction layered on top of the canonical
 * HouseConfig: every door (exterior, interior) and every casement window is
 * wrapped at build time in a pivot `Group` named `Hinge_<id>`. The pivot
 * group has its origin coincident with the hinge edge so a single
 * `rotation.y = θ` on the group is enough to swing the leaf open.
 *
 * Hinges are NEVER serialised into HouseConfig — that would force a
 * regenerate every time a slider moves, and would also pollute share links
 * with transient interaction state. Instead each rebuild collects the live
 * set of HingeDef into the BuiltHouse, which the App pushes into the
 * `useHingesStore`. Sliders mutate the store; an effect then drives the
 * actual scene-graph rotations via `applyHingeAngle`.
 *
 * USDA export reads the same store + scene graph so the exported file
 * preserves both the limits AND the user's currently-set angle.
 */

export type HingeAxis = 'X' | 'Y' | 'Z';

export interface HingeDef {
  /** Stable per-house ID, used as the slider key and the prim name. */
  id: string;
  /** Human-readable label shown in the UI ("Front Door", "L0 Living Window 1"). */
  label: string;
  /** Loose category for grouping in the UI panel. */
  category: 'exterior-door' | 'interior-door' | 'window' | 'gate';
  /** Local rotation axis on the hinge group. */
  axis: HingeAxis;
  /** Min / max rotation in degrees. min < max, both can be negative. */
  minDeg: number;
  maxDeg: number;
  /**
   * Sign of "open" rotation. Some hinges open in +deg, others in -deg
   * depending on which side of the leaf the hinge is mounted on.
   * `t = 0` always means closed, `t = 1` always means fully open.
   */
  openSignFromMin: 0 | 1;
  /**
   * Optional default slider position for the first build that
   * introduces this hinge. Used e.g. by the front gate which looks
   * correct half-open. Omitted → defaults to 0 (closed).
   */
  defaultT?: number;
}

/** Pure helper: convert a normalised slider value (0..1) to degrees per def. */
export function hingeDegFromT(def: HingeDef, t: number): number {
  const u = clamp01(t);
  return def.openSignFromMin === 0
    ? def.minDeg + (def.maxDeg - def.minDeg) * u
    : def.maxDeg + (def.minDeg - def.maxDeg) * u;
}

function clamp01(v: number): number {
  return Math.max(0, Math.min(1, v));
}

/** Find every Hinge_<id> group in a subtree. Order of traversal is stable
 *  for a given scene topology, which matters for export determinism. */
export function findHinges(root: Object3D): Array<{ id: string; group: Group; def: HingeDef }> {
  const out: Array<{ id: string; group: Group; def: HingeDef }> = [];
  root.traverse((n) => {
    if (!n.name.startsWith('Hinge_')) return;
    if (!(n instanceof Group)) return;
    const def = n.userData?.hinge as HingeDef | undefined;
    if (!def) return;
    out.push({ id: def.id, group: n, def });
  });
  return out;
}

/** Apply a normalised value (0..1) to a single hinge group, optionally
 *  inverting the swing direction. Inverted = same hinge edge, but rotates
 *  the opposite way (e.g. a door swinging into the OTHER room). The min /
 *  max degree limits keep the same magnitude. */
export function applyHingeAngle(
  group: Group,
  def: HingeDef,
  t: number,
  inverted = false,
): void {
  const sign = inverted ? -1 : 1;
  const rad = (sign * hingeDegFromT(def, t) * Math.PI) / 180;
  group.rotation.set(0, 0, 0);
  switch (def.axis) {
    case 'X':
      group.rotation.x = rad;
      break;
    case 'Y':
      group.rotation.y = rad;
      break;
    case 'Z':
      group.rotation.z = rad;
      break;
  }
}
